import edapWrapper from '@homeaway/edap-wrapper';
import EvaluationData from './EvaluationData';
import * as utils from './utils';

/* eslint no-use-before-define: ["error", { "functions": false }]*/

/**
 * Keep track of exposed experiments to avoid sending extra unneeded EDAP events
 * @private
 */
let exposedExperiments = {};

/**
 * Initialize a global `__ABACUS__` object with the tests passed into the constructor.
 * The reason a global variable is used is so that shared components, which also
 * use @homeaway/abacus-client-utils (but perhaps different versions), all reference
 * the same dictionary of tests. Cast these test objects as {@link EvaluationData} objects.
 *
 * @param {Object<string, EvaluationData>} tests - a dictionary of test names to evaluation data objects
 */
export function initialize(tests) {
    if (typeof window === 'undefined') {
        return;
    }

    // Reset the map of exposed experiments
    exposedExperiments = {};

    // Save any experiments queued for exposure logging
    let exposuresToLog = [];
    if (Array.isArray(window.__ABACUS__)) {
        exposuresToLog = window.__ABACUS__;
        window.__ABACUS__ = null;
    }

    if (tests && !window.__ABACUS__) {
        const experimentCache = Object.keys(tests).reduce((obj, testName) => {
            obj[testName] = new EvaluationData(tests[testName]);
            return obj;
        }, {});

        // Proxy calls of `__ABACUS__.push(expName1[, ...[, expNameN]])` to `evaluateExperimentAndLog(expNameN)`
        Object.defineProperty(experimentCache, 'push', {
            value: (...experimentNames) => {
                experimentNames.forEach((experimentName) => {
                    evaluateExperimentAndLog(experimentName);
                });
            }
        });

        window.__ABACUS__ = experimentCache;
    }

    // Log queued exposures
    exposuresToLog.forEach((experimentName) =>
        evaluateExperimentAndLog(experimentName)
    );
}

/**
 * Returns an instance of EvaluationData for the specified experiment name. This method does not log the
 * evaluation. Please ensure you understand how to separate evaluation from logging before using this method. Always
 * prefer to use {@link evaluateExperimentAndLog|evaluateExperimentAndLog()}.
 *
 * @param {string} experimentName - the name of the experiment to evaluate
 * @returns {EvaluationData}
 */
export function evaluateExperiment(experimentName) {
    if (typeof window === 'undefined') {
        return;
    }

    const evaluationData = window.__ABACUS__[experimentName];
    if (evaluationData) {
        return evaluationData; // eslint-disable-line consistent-return
    }

    return utils.getEvaluationDataWithException(); // eslint-disable-line consistent-return
}

/**
 * Returns an instance of EvaluationData for all experiments running on the page. This method does not log the
 * evaluation. To evaluate specified experiment name without logging use {@link evaluateExperiment|evaluateExperiment()}.
 *
 * @returns {EvaluationData}
 */
export function evaluateAllExperiments() {
    if (typeof window === 'undefined') {
        return;
    }

    const evaluationDatas = window.__ABACUS__ || {};
    if (Object.keys(evaluationDatas).length > 0) {
        return evaluationDatas; // eslint-disable-line consistent-return
    }

    return utils.getEmptyEvaluationData(); // eslint-disable-line consistent-return
}

/**
 * Retrieves an already evaluated experiment, logs the exposure of an experiment, and returns the result as an EvaluationData object. If any
 * errors occur than the resulting EvaluationData will contain details of the error and the Abacus bucket value will
 * refer to the control bucket.
 *
 * @param {string} experimentName - the name of the experiment to evaluate
 * @param {object} [edapTriggerOptions] - The `triggerOptions` object that is applied to the EDAP `test.exposure` event. See {@link https://github.homeawaycorp.com/edap/edap-integrations/blob/master/API.md#triggereventname-data-opts|triggerOptions}.
 * @param {object} [edapData] - Contains any additional properties to include in the edap event
 * @returns {EvaluationData}
 */
export function evaluateExperimentAndLog(
    experimentName,
    edapTriggerOptions,
    edapData
) {
    if (typeof window === 'undefined') {
        return;
    }

    const evaluationData = evaluateExperiment(experimentName);

    if (evaluationData) {
        logExperiment(evaluationData, edapTriggerOptions, edapData);
    }

    return evaluationData; // eslint-disable-line consistent-return
}

/* eslint-disable complexity */
/**
 * Logs an already evaluated experiment. Keeps track of which experiments have been logged so that the exposure event is
 * triggered at most once per test per pageload.
 *
 * Please ensure you understand how to separate evaluation from logging before using this method. Your experiment will
 * be invalid if the passed in EvaluationData has not come as a result of calling evaluateExperiment().
 *
 * @param {EvaluationData|string} evaluationData - Object that contains information about the evaluated test. Alternatively, supply an experiment name.
 * @param {object} [edapTriggerOptions] - The `triggerOptions` object that is applied to the EDAP `test.exposure` event. See {@link https://github.homeawaycorp.com/edap/edap-integrations/blob/master/API.md#triggereventname-data-opts|triggerOptions}.
 * @param {object} [edapData] - Contains any additional properties to include in the edap event
 */
export function logExperiment(evaluationData, edapTriggerOptions, edapData) {
    if (typeof window === 'undefined' || !evaluationData) {
        return;
    }

    if (typeof evaluationData === 'string') {
        evaluateExperimentAndLog(evaluationData, edapTriggerOptions, edapData);
        return;
    }

    if (evaluationData.exception) {
        return;
    }

    const experimentName = evaluationData.experimentKey.experimentName;

    if (exposedExperiments.hasOwnProperty(experimentName)) {
        return;
    }

    evaluationData = new EvaluationData(evaluationData);

    edapData = edapData || {};
    edapData.testname = `${evaluationData.experimentKey.experimentName}:${
        evaluationData.experimentInstanceId
    }`;
    edapData.variant = `${evaluationData.value}`;
    if (evaluationData.entityId) {
        edapData.entityid = evaluationData.entityId;
    }

    edapWrapper().push((edap) => {
        edap.trigger('test.exposure', edapData, edapTriggerOptions);
    });

    exposedExperiments[experimentName] = true;
}
/* eslint-enable complexity */

export default {
    initialize,
    evaluateExperiment,
    evaluateAllExperiments,
    evaluateExperimentAndLog,
    logExperiment
};
